// analytics.js - Analytics tracking utilities for Focal Path

import cookieManager from './cookieManager.js';

class Analytics {
    constructor() {
        this.isInitialized = false;
        this.eventQueue = [];
        this.init();
    }

    /**
     * Check if running in development/local environment
     * Includes subdomain variants of localhost for testing
     */
    isDevelopment() {
        const hostname = window.location.hostname.toLowerCase();
        return hostname === 'localhost' 
            || hostname === '127.0.0.1'
            || hostname.endsWith('.localhost');  // Matches toppriority.localhost, etc.
    }

    init() {
        // Mark session start
        cookieManager.markSessionStart();
        
        // Set up page visibility tracking
        this.setupVisibilityTracking();
        
        // Set up error tracking
        this.setupErrorTracking();
        
        // Set up unload tracking
        this.setupUnloadTracking();
        
        this.isInitialized = true;
        
        // Process any queued events
        this.processEventQueue();
    }

    // === CORE TRACKING METHODS ===

    /**
     * Track a custom event
     * @param {string} eventName - Name of the event
     * @param {Object} properties - Event properties
     * @param {Object} options - Tracking options
     */
    track(eventName, properties = {}, options = {}) {
        const event = {
            event: eventName,
            properties: {
                ...properties,
                timestamp: Date.now(),
                url: window.location.pathname,
                hostname: window.location.hostname,
                isTopPrioritySubdomain: window.location.hostname.toLowerCase().startsWith('toppriority.'),
                referrer: document.referrer,
                userAgent: navigator.userAgent,
                sessionId: cookieManager.sessionId,
                userId: cookieManager.userId
            },
            options
        };

        if (this.isInitialized) {
            this.sendEvent(event);
        } else {
            this.eventQueue.push(event);
        }
    }

    /**
     * Track page view
     * @param {string} pageName - Name of the page
     * @param {Object} properties - Additional properties
     */
    trackPageView(pageName, properties = {}) {
        this.track('page_view', {
            page: pageName,
            title: document.title,
            ...properties
        });
    }

    /**
     * Track user interaction
     * @param {string} element - Element interacted with
     * @param {string} action - Action performed
     * @param {Object} properties - Additional properties
     */
    trackInteraction(element, action, properties = {}) {
        this.track('user_interaction', {
            element,
            action,
            ...properties
        });
    }

    // === FOCAL PATH SPECIFIC TRACKING ===

    /**
     * Track decision journey progress
     * @param {string} stage - Current stage of decision
     * @param {Object} data - Stage-specific data
     */
    trackDecisionStage(stage, data = {}) {
        this.track('decision_stage', {
            stage,
            ...data
        });
    }

    /**
     * Track dilemma input
     * @param {Object} dilemmaData - Dilemma information
     */
    trackDilemmaInput(dilemmaData) {
        this.trackDecisionStage('dilemma_input', {
            dilemmaLength: dilemmaData.dilemma?.length || 0,
            hasContent: !!dilemmaData.dilemma?.trim()
        });
    }

    /**
     * Track path creation
     * @param {Object} pathData - Path information
     */
    trackPathCreation(pathData) {
        this.trackDecisionStage('path_creation', {
            pathCount: Object.keys(pathData.pathNames || {}).length,
            pathNames: Object.values(pathData.pathNames || {})
        });
    }

    /**
     * Track criteria selection
     * @param {Object} criteriaData - Criteria information
     */
    trackCriteriaSelection(criteriaData) {
        this.trackDecisionStage('criteria_selection', {
            criteriaCount: criteriaData.criteria?.length || 0,
            criteriaTypes: criteriaData.criteriaCategorization || [],
            suggestedUsed: criteriaData.suggestedCriteriaUsed || 0,
            customCreated: criteriaData.customCriteriaCreated || 0
        });
    }

    /**
     * Track path evaluation
     * @param {Object} evaluationData - Evaluation information
     */
    trackPathEvaluation(evaluationData) {
        this.trackDecisionStage('path_evaluation', {
            criterionName: evaluationData.criterionName,
            pathValues: evaluationData.pathValues,
            timeSpent: evaluationData.timeSpent
        });
    }

    /**
     * Track decision completion
     * @param {Object} completionData - Completion information
     */
    trackDecisionCompletion(completionData) {
        const sessionDuration = cookieManager.getSessionDuration();
        
        this.track('decision_completed', {
            sessionDuration,
            pathCount: Object.keys(completionData.pathNames || {}).length,
            criteriaCount: completionData.criteria?.length || 0,
            usedTips: completionData.usedTips || false,
            usedExample: completionData.usedExample || false,
            winningPath: completionData.winningPath,
            results: completionData.results
        });

        // Also save to cookie manager for additional processing
        cookieManager.trackDecisionCompletion(completionData);
    }

    /**
     * Track feature usage
     * @param {string} feature - Feature name
     * @param {Object} data - Feature-specific data
     */
    trackFeatureUsage(feature, data = {}) {
        this.track('feature_usage', {
            feature,
            ...data
        });
    }

    /**
     * Track tips interaction
     * @param {string} action - Action (show/hide/toggle)
     * @param {string} context - Context where tips were used
     */
    trackTipsUsage(action, context) {
        this.trackFeatureUsage('tips', {
            action,
            context
        });
    }

    /**
     * Track example usage
     * @param {string} action - Action (show/hide/toggle)
     * @param {string} exampleType - Type of example
     */
    trackExampleUsage(action, exampleType = 'default') {
        this.trackFeatureUsage('example', {
            action,
            exampleType
        });
    }

    /**
     * Track screenshot download
     * @param {Object} data - Screenshot data
     */
    trackScreenshotDownload(data = {}) {
        this.trackFeatureUsage('screenshot_download', {
            format: data.format || 'png',
            success: data.success !== false
        });
    }

    // === ERROR TRACKING ===

    /**
     * Track JavaScript errors
     * @param {Error} error - Error object
     * @param {Object} context - Additional context
     */
    trackError(error, context = {}) {
        this.track('error', {
            message: error.message,
            stack: error.stack,
            filename: error.filename,
            lineno: error.lineno,
            colno: error.colno,
            ...context
        });
    }

    /**
     * Track API errors
     * @param {string} endpoint - API endpoint
     * @param {number} status - HTTP status code
     * @param {string} message - Error message
     */
    trackApiError(endpoint, status, message) {
        this.track('api_error', {
            endpoint,
            status,
            message
        });
    }

    // === PERFORMANCE TRACKING ===

    /**
     * Track page load performance
     */
    trackPagePerformance() {
        if ('performance' in window) {
            const navigation = performance.getEntriesByType('navigation')[0];
            
            this.track('page_performance', {
                loadTime: navigation.loadEventEnd - navigation.loadEventStart,
                domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
                timeToFirstByte: navigation.responseStart - navigation.requestStart,
                dnsLookup: navigation.domainLookupEnd - navigation.domainLookupStart
            });
        }
    }

    /**
     * Track user engagement metrics
     * @param {Object} metrics - Engagement metrics
     */
    trackEngagement(metrics) {
        this.track('user_engagement', {
            timeOnPage: metrics.timeOnPage,
            scrollDepth: metrics.scrollDepth,
            clickCount: metrics.clickCount,
            keystrokes: metrics.keystrokes
        });
    }

    // === CONVERSION TRACKING ===

    /**
     * Track conversion funnel step
     * @param {string} step - Funnel step name
     * @param {Object} data - Step-specific data
     */
    trackFunnelStep(step, data = {}) {
        this.track('funnel_step', {
            step,
            timestamp: Date.now(),
            ...data
        });
    }

    /**
     * Track form abandonment
     * @param {string} formName - Name of the form
     * @param {Object} data - Form data
     */
    trackFormAbandonment(formName, data = {}) {
        this.track('form_abandonment', {
            form: formName,
            fieldsCompleted: data.fieldsCompleted || 0,
            totalFields: data.totalFields || 0,
            timeSpent: data.timeSpent || 0
        });
    }

    // === SETUP METHODS ===

    setupVisibilityTracking() {
        let visibilityStart = Date.now();
        
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                const timeVisible = Date.now() - visibilityStart;
                this.track('page_visibility', {
                    action: 'hidden',
                    timeVisible
                });
            } else {
                visibilityStart = Date.now();
                this.track('page_visibility', {
                    action: 'visible'
                });
            }
        });
    }

    setupErrorTracking() {
        window.addEventListener('error', (event) => {
            this.trackError(event.error, {
                type: 'javascript_error',
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno
            });
        });

        window.addEventListener('unhandledrejection', (event) => {
            this.trackError(new Error(event.reason), {
                type: 'unhandled_promise_rejection'
            });
        });
    }

    setupUnloadTracking() {
        window.addEventListener('beforeunload', () => {
            // Send any remaining events
            this.flushEvents();
            
            // Track session end
            this.track('session_end', {
                sessionDuration: cookieManager.getSessionDuration()
            });
        });
    }

    // === DATA PROCESSING ===

    processEventQueue() {
        while (this.eventQueue.length > 0) {
            const event = this.eventQueue.shift();
            this.sendEvent(event);
        }
    }

    /**
     * Send event to analytics endpoint
     * @param {Object} event - Event data
     */
    async sendEvent(event) {
        // Only send if user has given consent
        if (!cookieManager.consentGiven) return;

        try {
            // Add to cookie manager's interaction queue
            cookieManager.addToInteractionQueue(event);

            // Only send to server if we're not in development mode
            if (!this.isDevelopment()) {
                // Send to your analytics endpoint
                await fetch('/api/analytics', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(event)
                });
            }
        } catch (error) {
            // Silently fail in development, warn in production
            if (!this.isDevelopment()) {
                console.warn('Failed to send analytics event:', error);
            }
        }
    }

    /**
     * Flush all pending events
     */
    flushEvents() {
        const queue = cookieManager.getInteractionQueue();
        
        if (queue.length > 0 && !this.isDevelopment()) {
            // Send batch request only in production
            navigator.sendBeacon('/api/analytics/batch', JSON.stringify({
                events: queue,
                timestamp: Date.now()
            }));
        }
    }

    // === UTILITY METHODS ===

    /**
     * Get user journey data
     * @returns {Object} User journey information
     */
    getUserJourney() {
        const queue = JSON.parse(sessionStorage.getItem('fp_interaction_queue') || '[]');
        
        return {
            sessionId: cookieManager.sessionId,
            userId: cookieManager.userId,
            sessionDuration: cookieManager.getSessionDuration(),
            events: queue,
            currentPage: window.location.pathname
        };
    }

    /**
     * Set user properties
     * @param {Object} properties - User properties
     */
    setUserProperties(properties) {
        if (!cookieManager.consentGiven) return;

        this.track('user_properties_updated', {
            properties
        });
    }
}

// Export singleton instance
export default new Analytics();
